import config from '../config';

class HttpClient {
    constructor(baseURL, tenantId) {
        this.baseURL = baseURL;
        this.tenantId = tenantId;
        this.onUnauthorized = null;
        this.onSessionExpired = null;
        this.isRefreshing = false;
        this.refreshPromise = null;
    }

    setToken(token) {
        this.token = token;
    }

    setOnUnauthorized(callback) {
        this.onUnauthorized = callback;
    }

    setOnSessionExpired(callback) {
        this.onSessionExpired = callback;
    }

    async tryRefreshToken() {
        // Se já está tentando refresh, retorna a promise existente
        if (this.isRefreshing && this.refreshPromise) {
            return this.refreshPromise;
        }

        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            // Se não tem refresh token, dispara sessão expirada
            if (this.onSessionExpired) {
                this.onSessionExpired();
            }
            return Promise.resolve(false);
        }

        // Marca como refreshing e cria promise
        this.isRefreshing = true;
        this.refreshPromise = (async () => {
            try {
                const response = await fetch(`${this.baseURL}/usuarios/refresh`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Tenant-ID': this.tenantId,
                    },
                    body: JSON.stringify({ refreshToken }),
                });

                if (response.ok) {
                    // Tenta ler o corpo da resposta apenas se houver conteúdo
                    const contentType = response.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                        try {
                            const data = await response.json();
                            const { token } = data;

                            if (token) {
                                localStorage.setItem('token', token);
                                this.setToken(token);
                                this.isRefreshing = false;
                                this.refreshPromise = null;
                                return true;
                            }
                        } catch (jsonError) {
                            // Se não conseguir fazer parse do JSON, considera como falha
                        }
                    } else {
                        // Se não tem JSON, considera como falha
                    }
                }
                
                // Se a resposta não foi ok ou não retornou token, dispara sessão expirada
                if (this.onSessionExpired) {
                    this.onSessionExpired();
                }
            } catch (error) {
                // Em caso de erro, também dispara sessão expirada
                if (this.onSessionExpired) {
                    this.onSessionExpired();
                }
            } finally {
                this.isRefreshing = false;
                this.refreshPromise = null;
            }
            return false;
        })();

        return this.refreshPromise;
    }

    async request(path, options = {}) {
        const { skipAuth = false, headers: headersInit, ...fetchOptions } = options;
        const headers = new Headers(headersInit || {});

        // Add Tenant ID header
        headers.append('X-Tenant-ID', this.tenantId);

        // Add Authorization header if token exists (consultas públicas omitem o JWT)
        if (!skipAuth) {
            const token = this.token || localStorage.getItem('token');
            if (token) {
                headers.append('Authorization', `Bearer ${token}`);
            }
        }

        // JSON só quando o body não for FormData — o browser define o boundary do multipart
        const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;
        if (fetchOptions.body && !headers.has('Content-Type') && !isFormData) {
            headers.append('Content-Type', 'application/json');
        }

        const response = await fetch(`${this.baseURL}${path}`, {
            ...fetchOptions,
            headers,
        });

        if (skipAuth) {
            return response;
        }

        // Intercepta respostas 401 (não autorizado) - Logout imediato
        if (response.status === 401 && this.onUnauthorized) {
            this.onUnauthorized();
            return response;
        }

        // Intercepta respostas 403 (proibido/expirado) - Tenta refresh
        if (response.status === 403) {
            const refreshed = await this.tryRefreshToken();

            if (refreshed) {
                // Se renovou, tenta a requisição original novamente com novo token
                const newToken = this.token || localStorage.getItem('token');
                headers.set('Authorization', `Bearer ${newToken}`);

                return await fetch(`${this.baseURL}${path}`, {
                    ...fetchOptions,
                    headers,
                });
            } else {
                // Se falhou o refresh, dispara sessão expirada
                if (this.onSessionExpired) {
                    this.onSessionExpired();
                } else if (this.onUnauthorized) {
                    this.onUnauthorized();
                }
            }
        }

        return response;
    }

    get(path, options = {}) {
        return this.request(path, { ...options, method: 'GET' });
    }

    post(path, body, options = {}) {
        return this.request(path, {
            ...options,
            method: 'POST',
            body: JSON.stringify(body),
        });
    }

    put(path, body, options = {}) {
        return this.request(path, {
            ...options,
            method: 'PUT',
            body: JSON.stringify(body),
        });
    }

    patch(path, body, options = {}) {
        return this.request(path, {
            ...options,
            method: 'PATCH',
            body: body ? JSON.stringify(body) : null,
        });
    }

    delete(path, options = {}) {
        return this.request(path, { ...options, method: 'DELETE' });
    }
}

const httpClient = new HttpClient(config.apiUrl, config.tenantId);

export default httpClient;
